import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "../../api/api";

export default function PaymentSuccessPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [status, setStatus] = useState("loading");
    const [countdown, setCountdown] = useState(5);

    const paymentIntent = searchParams.get("payment_intent");
    const orderId = searchParams.get("orderId");
    const shipmentId = searchParams.get("shipmentId");

    const targetUrl = useMemo(() => {
        if (orderId) return `/client/orders/${orderId}`;
        if (shipmentId) return `/client/orders?shipmentId=${shipmentId}`;
        return "/client/orders";
    }, [orderId, shipmentId]);

    useEffect(() => {
        if (!paymentIntent) {
            setStatus("error");
            return;
        }

        let interval;

        const checkStatus = async () => {
            try {
                const res = await api.get(`/stripe/status/${paymentIntent}`);

                if (res.data?.status === "Completed") {
                    setStatus("success");
                    clearInterval(interval);
                } else {
                    setStatus("pending");
                }
            } catch {
                setStatus("error");
                clearInterval(interval);
            }
        };

        checkStatus();
        interval = setInterval(checkStatus, 3000);

        return () => clearInterval(interval);
    }, [paymentIntent]);

    useEffect(() => {
        if (status !== "success") return;

        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    navigate(targetUrl);
                    return 0;
                }

                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [status, targetUrl, navigate]);

    if (status === "loading") {
        return <p className="text-center mt-10">Checking payment…</p>;
    }

    if (status === "pending") {
        return (
            <div className="text-center mt-10">
                <h2 className="text-yellow-600 text-xl">
                    ⏳ Waiting for confirmation...
                </h2>
                <p>Do not close this page</p>
            </div>
        );
    }

    if (status === "error") {
        return (
            <div className="text-center mt-10">
                <h2 className="text-red-600 text-xl">
                    ⚠ Error checking payment
                </h2>

                <Link
                    to="/client/orders"
                    className="mt-4 inline-block bg-gray-600 text-white px-4 py-2 rounded"
                >
                    Back to Orders
                </Link>
            </div>
        );
    }

    return (
        <div className="text-center mt-10">
            <h1 className="text-green-600 text-2xl font-bold">
                ✅ Payment successful
            </h1>

            <p className="mt-2 text-gray-600">
                Redirecting in {countdown} seconds...
            </p>

            <div className="mt-6 flex gap-3 justify-center">
                <Link
                    to={targetUrl}
                    className="bg-indigo-600 text-white px-4 py-2 rounded"
                >
                    Go to Order
                </Link>

                <Link
                    to="/client/orders"
                    className="bg-gray-400 text-white px-4 py-2 rounded"
                >
                    All Orders
                </Link>
            </div>
        </div>
    );
}